import { useEffect, useRef } from 'react';
import type { DocumentData, Unsubscribe } from 'firebase/firestore';

import { importWithReload } from '@/lib/chunkReload';

import { register, unregister } from './listenerRegistry';

type FirestoreRuntime = typeof import('./firestoreRuntime');

/**
 * R413 — Firebase SDK 不進主 bundle:第一次訂閱時才載入(之後是同一份 module,不重載)。
 * ⚠️ 不要改回靜態 import —— 官網 / 登入頁會重新多下載、解析 ≈ 640 KB(見 `session.ts`)。
 * 換版後的舊分頁載不到舊 chunk → 重載一次拿新版(`importWithReload`);再失敗才走 `onError`。
 *
 * **同一個分頁共用同一次載入**:畫布頁一進來就同時掛好幾個訂閱(錢包 / job / 圖同步)。
 * 各自載入的話,載入失敗時只有第一個拿到「重載一次」的額度,其餘幾個會在頁面重載前各自走
 * `onError`、閃一下錯誤提示。失敗時清掉快取,之後的訂閱還能重試(例:網路恢復)。
 */
let runtimeLoad: Promise<FirestoreRuntime> | null = null;
function loadFirestoreRuntime(): Promise<FirestoreRuntime> {
  if (!runtimeLoad) {
    const p = importWithReload('firestoreRuntime', () => import('./firestoreRuntime'));
    runtimeLoad = p;
    p.catch(() => {
      if (runtimeLoad === p) runtimeLoad = null;
    });
  }
  return runtimeLoad;
}

/**
 * Firestore listener 集中 hook(R36 mock-wired,R37 接真 SDK,R41.6 砍 mock 分流)。
 *
 * 對齊 {@link ../../../docs/decisions/ADR-002-firebase-auth-coexistence.md ADR-002}
 * + SSOT 第二份 §3-§4:
 * - **path 由 BE 算**:`GET /api/projects/{pid}/jobs/{jid}` Response 帶 `firestore_paths`,
 *   FE 不自拼(Anti-pattern #2)。本 hook 接 caller 給的 path string。
 * - **集中走本 hook**:避免 raw `onSnapshot` 漏 unsubscribe → 配額爆 + stale data
 *   殘留(Security panel red line);ESLint rule `hai/no-raw-onsnapshot` 強制走這裡。
 * - **mount → register listener,unmount → unregister + detach**;`onAuthLost` 走
 *   `listenerRegistry.detachAll()` 一次清掉所有。
 *
 * 流程:
 *   0. 動態載入 Firebase SDK(R413;載入失敗走 `onError`,與 sign-in 失敗同一條出口)
 *   1. 確保 Firebase signed in(BE 簽 custom token → SDK signInWithCustomToken)
 *   2. attach Firestore SDK `onSnapshot`(`doc` 或 `collection`)
 *   3. unmount → unsubscribe + unregister
 */

export type FirestoreSubscriptionType = 'doc' | 'collection';

export interface FirestoreSubscriptionOptions<T> {
  /** BE 算好的 Firestore path(SSOT §3 `firestore_paths.{project,job,steps_collection,assets_collection}` 對齊)。 */
  path: string;
  /** `'doc'` 訂單一 document / `'collection'` 訂整個 collection(後者預期 BE 端帶 ordering)。 */
  type: FirestoreSubscriptionType;
  /** 收到 snapshot 時的 callback;`null` 代表 doc 不存在(僅 `type='doc'` 場景)。 */
  onSnapshot: (data: T | null) => void;
  /** Listener 錯誤(permission-denied / unavailable / etc)callback。可選;不給時 hook 內 throw。 */
  onError?: (err: Error) => void;
}

/**
 * Subscribe Firestore doc / collection,自動管理 register / unregister / detach。
 *
 * @param opts {@link FirestoreSubscriptionOptions}(`path` / `type` / callbacks)
 */
export function useFirestoreSubscription<T = DocumentData>(
  opts: FirestoreSubscriptionOptions<T>,
): void {
  // 把 callbacks 存 ref,避免 caller 每 render 給新 function reference 觸發 effect
  // 重啟 listener(對 firebase quota 友善)。
  const onSnapshotRef = useRef(opts.onSnapshot);
  const onErrorRef = useRef(opts.onError);
  onSnapshotRef.current = opts.onSnapshot;
  onErrorRef.current = opts.onError;

  useEffect(() => {
    // empty path 表示 caller 還沒拿到 firestorePaths(reload 中段 / 沒 active job);
    // effect 不 attach,caller 拿到 paths 後 hook re-run 才 attach
    if (!opts.path) return;

    let mounted = true;
    let unsub: Unsubscribe = () => {};

    void (async () => {
      // 必須先 ensure Firebase signed in 才能讓 onSnapshot 帶 auth.uid 通過 Rules 校驗
      let rt: FirestoreRuntime;
      try {
        rt = await loadFirestoreRuntime();
        await rt.ensureFirebaseSignIn();
      } catch (err) {
        if (mounted) onErrorRef.current?.(err as Error);
        return;
      }
      if (!mounted) return;

      if (opts.type === 'doc') {
        // path 由 BE `firestore_paths` 提供;FE 不自拼(SSOT 第二份 §3 / Anti-pattern #2)
        const ref = rt.doc(rt.firestore, opts.path);
        unsub = rt.onSnapshot(
          ref,
          (snap) => {
            const data = snap.exists() ? (snap.data() as T) : null;
            onSnapshotRef.current(data);
          },
          (err: Error) => {
            onErrorRef.current?.(err);
          },
        );
      } else {
        const ref = rt.collection(rt.firestore, opts.path);
        unsub = rt.onSnapshot(
          ref,
          (snap) => {
            // collection 模式:把每個 doc 攤成 `{ id, ...data }` 陣列。`T` 是 caller 宣告的
            // 任意 shape(通常是某個 entity 陣列),compiler 無從證明這個結構化陣列可賦值給
            // 不受限的 `T` → 單層 `as T` 會被 TS2352 擋,`as unknown as` 是必要的橋接。
            const docs = snap.docs.map((d) => ({
              id: d.id,
              ...(d.data() as object),
            })) as unknown as T;
            onSnapshotRef.current(docs);
          },
          (err: Error) => {
            onErrorRef.current?.(err);
          },
        );
      }

      register(unsub);
    })();

    return () => {
      mounted = false;
      try {
        unsub();
      } finally {
        unregister(unsub);
      }
    };
  }, [opts.path, opts.type]);
}
